import Toast from '@/components/toast';
import { generateUUID, compareVersions } from '@/utils/tools';
import eventBus from '@/const/eventBus';
import { getVersion } from '@/utils/systemCode';
import { platformAdsFlowAction } from './bridge';
import { handleFinishTask } from './helper';

// 模块级会话互斥锁：同一时刻只允许一个活跃会话
let activeSessionId = null;

/**
 * 连续看视频引擎入口
 * @param {object} config - 配置对象
 * @param {object} config.firstVideoParams - 首次视频参数（必填）
 * @param {Function} config.getNextVideoParams - 获取下一个视频参数的策略函数（必填）
 * @param {number} [config.maxCount=0] - 本轮最多连续观看视频数量，0 表示不限制（可选）
 * @param {object} [config.bannerAdsInfo] - Banner 广告信息（可选）
 * @param {object} [config.toastConfig] - Toast 提示配置（可选）
 * @param {object} [config.callbacks] - 回调钩子（可选）
 * @returns {Promise<{
 *  success: boolean,
 *  completedCount: number,
 *  exitScene: string,
 *  adLoadFailed: boolean
 * }>}
 */
// eslint-disable-next-line import/prefer-default-export
export async function playContinuousVideo(config = {}) {
  const {
    firstVideoParams,
    getNextVideoParams,
    maxCount = 0,
    bannerAdsInfo = {},
    toastConfig,
    callbacks = {},
    finishTaskInfo,
  } = config;

  const version = await getVersion();
  if (compareVersions(version, '6.99.30') === -1) {
    Toast.show('当前APP版本不支持，请下载最新版本应用');
    return { success: false };
  }

  // 参数校验：缺少必填参数时直接返回失败
  if (!firstVideoParams || !getNextVideoParams) {
    return { success: false };
  }

  // 会话互斥锁：正在进行中的会话拒绝新调用
  if (activeSessionId) {
    return { success: false };
  }

  // 生成唯一 sessionId
  const sessionId = generateUUID();
  activeSessionId = sessionId;

  // 会话内部状态
  let currentIndex = 0;
  let currentParams = firstVideoParams;
  let completedCount = 0;

  // 通过 platformAdsFlowAction 发起首次 playVideo Bridge 调用
  const playRes = await platformAdsFlowAction({
    sessionId,
    event: 'playVideo',
    adsInfo: {
      adUnitId: firstVideoParams.adUnitId,
      taskCode: firstVideoParams.taskCode,
      business_line: firstVideoParams.business_line || 'mars',
      rewardCount: firstVideoParams.rewardCount,
    },
    timeout: 3,
  });

  // 首次 Bridge 调用返回 errCode 表示加载失败
  if (playRes && playRes.errCode) {
    activeSessionId = null;
    const result = {
      success: false,
      completedCount: 0,
      exitScene: 'none_completed_failed',
      adLoadFailed: true,
    };
    // Toast 配置映射
    if (toastConfig && toastConfig.none_completed_failed) {
      Toast.show(toastConfig.none_completed_failed);
    }
    // 回调钩子
    if (callbacks.onSessionEnd) {
      callbacks.onSessionEnd(result);
    }
    return result;
  }

  // 返回 Promise，通过 eventBus 监听后续事件来 resolve
  return new Promise((resolve) => {
    // 存储 reward 处理时 peek 到的下一个视频参数，供 nextVideo action 使用
    let peekedNextParams = null;

    /**
     * 清理函数：取消 eventBus 监听器并释放会话锁
     */
    const cleanup = () => {
      // eslint-disable-next-line no-use-before-define
      eventBus.$off('platformAdsFlowEvent', onFlowEvent);
      activeSessionId = null;
    };

    /**
     * 结束会话的统一出口
     * @param {object} result - SessionResult
     */
    const endSession = (result) => {
      cleanup();
      // Toast 配置映射
      if (toastConfig && result.exitScene && toastConfig[result.exitScene]) {
        Toast.show(toastConfig[result.exitScene]);
      }
      // onSessionEnd 回调
      if (callbacks.onSessionEnd) {
        callbacks.onSessionEnd(result);
      }
      resolve(result);
    };

    /**
     * platformAdsFlowEvent 事件处理器
     * 后续任务（1.2、1.3、1.4）将填充完整的事件处理逻辑
     */
    const onFlowEvent = async (params) => {
      const {
        sessionId: sid, event, type, data,
      } = params || {};

      // sessionId 过滤：忽略不匹配的事件
      if (sid !== sessionId) return;

      // === playVideo 事件处理 ===
      if (event === 'playVideo') {
        if (type === 'reward') {
          // 验证 verify 字段：未通过验证时不推进，直接返回
          if (!data || !data.verify) {
            return;
          }

          // 递增已完成视频计数
          completedCount += 1;

          // 达到本轮最大观看数量时，不再获取下一个视频
          let nextParams = null;
          if (maxCount > 0 && completedCount >= maxCount) {
            nextParams = null;
          } else {
            // 调用 getNextVideoParams 获取下一个视频参数（用于弹窗展示 nextRewardCount）
            try {
              const nextResult = getNextVideoParams({ currentIndex, currentParams });
              nextParams = nextResult && typeof nextResult.then === 'function'
                ? await nextResult
                : nextResult;
            } catch (e) {
              // getNextVideoParams 异常时视为没有下一个
              nextParams = null;
            }
          }

          const isEnd = !nextParams;

          // 存储 nextParams 供 showResult action handler 使用
          peekedNextParams = nextParams;

          // 调用 onVideoComplete 回调
          if (callbacks.onVideoComplete) {
            callbacks.onVideoComplete({ currentParams, nextParams, currentIndex });
          }

          // 通过 platformAdsFlowAction 发送 showResult 事件
          await platformAdsFlowAction({
            sessionId,
            event: 'showResult',
            adsInfo: bannerAdsInfo || {},
            otherInfo: {
              data: {
                rewardCount: currentParams.rewardCount || '',
                nextRewardCount: (nextParams && nextParams.rewardCount) || '',
                isEnd,
              },
            },
            timeout: 3,
          });

          // 调用 onPopupExpose 回调
          if (callbacks.onPopupExpose) {
            callbacks.onPopupExpose({ currentParams, isEnd, bannerAdsInfo: bannerAdsInfo || {} });
          }
        } else if (type === 'failed') {
          // 广告加载失败：区分首次/非首次失败的 exitScene
          const exitScene = completedCount === 0 ? 'none_completed_failed' : 'mid_video_failed';
          endSession({
            success: completedCount > 0,
            completedCount,
            exitScene,
            adLoadFailed: true,
          });
        } else if (type === 'closed') {
          // 用户关闭视频：区分首次/非首次关闭的 exitScene
          const exitScene = completedCount === 0 ? 'none_completed_closed' : 'mid_video_closed';
          endSession({
            success: completedCount > 0,
            completedCount,
            exitScene,
            adLoadFailed: false,
          });
        }
      }

      // === showResult 用户操作处理 ===
      if (event === 'showResult' && type === 'action') {
        const { action, adsInfo: actionAdsInfo } = data || {};

        // 调用 onAction 回调传入操作信息
        if (callbacks.onAction) {
          callbacks.onAction({ action, data });
        }

        switch (action) {
          case 'nextVideo': {
            // 使用 reward 阶段已 peek 到的 peekedNextParams
            if (!peekedNextParams) {
              // 没有下一个视频，结束会话
              endSession({
                success: completedCount > 0,
                completedCount,
                exitScene: 'all_completed',
                adLoadFailed: false,
              });
              return;
            }
            // 推进 currentIndex 和 currentParams
            currentIndex += 1;
            currentParams = peekedNextParams;
            // 发起新的 playVideo Bridge 调用
            await platformAdsFlowAction({
              sessionId,
              event: 'playVideo',
              adsInfo: {
                adUnitId: currentParams.adUnitId,
                taskCode: currentParams.taskCode,
                business_line: currentParams.business_line || 'mars',
                rewardCount: currentParams.rewardCount,
              },
              timeout: 3,
            });
            // 重置 peekedNextParams
            peekedNextParams = null;
            break;
          }
          case 'bannerReward':
          case 'videoBannerReward': {
            // 获取翻倍后的总奖励金额
            const rewardTotalCount = (actionAdsInfo || {}).rewardTotalCount
              || (actionAdsInfo || {}).rewardCount
              || '';
            // 使用已存储的 peekedNextParams 计算 nextRewardCount 和 isEnd
            const nextRewardCount = (peekedNextParams && peekedNextParams.rewardCount) || '';
            const isEnd = !peekedNextParams;
            // 推进任务完成
            if (finishTaskInfo) {
              await handleFinishTask({
                inspireType: 0,
                bannerTaskRelationCouponInfo: finishTaskInfo,
              });
            }
            // 发送包含 isTotalReward 的 showResult
            await platformAdsFlowAction({
              sessionId,
              event: 'showResult',
              otherInfo: {
                data: {
                  rewardCount: rewardTotalCount,
                  nextRewardCount,
                  isEnd,
                  isTotalReward: true,
                },
              },
              timeout: 3,
            });
            // 调用 onPopupExpose 回调
            if (callbacks.onPopupExpose) {
              callbacks.onPopupExpose({ currentParams, isEnd, bannerAdsInfo: bannerAdsInfo || {} });
            }
            break;
          }
          case 'close':
            endSession({
              success: completedCount > 0,
              completedCount,
              exitScene: 'popup_closed',
              adLoadFailed: false,
            });
            break;
          default:
            break;
        }
      }
    };

    // 注册 eventBus platformAdsFlowEvent 监听器
    eventBus.$on('platformAdsFlowEvent', onFlowEvent);
  });
}
